import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import { cva, VariantProps } from 'cva';
import { ReactNode, useEffect, useState } from 'react';

export interface AvatarProps {
  image?: string;
  name: string;
  className?: string;
  fallback?: ReactNode;
}

export type GCSUrl = string | undefined;

const avatarStyles = cva({
  base: 'flex items-center justify-center flex-shrink-0 border border-[#1a1c201a] overflow-hidden',
  variants: {
    size: {
      xs: 'w-6 h-6 text-xxs',
      sm: 'w-8 h-8 text-xs',
      md: 'w-[36px] h-[36px] text-sm',
      lg: 'w-12 h-12 text-xl',
      '3xl': 'w-20 h-20 text-2xl',
    },
    avatar: {
      true: 'bg-white',
      false: 'bg-blue-200',
    },
    shape: {
      round: 'rounded-full',
      square: 'rounded-[8px]',
    },
    type: {
      user: '',
      company: '',
    },
  },
  defaultVariants: {
    size: 'sm',
    shape: 'round',
    type: 'user',
  },
  compoundVariants: [
    {
      avatar: false,
      type: 'company',
      class: 'bg-gray-100',
    },
  ],
});

export const Avatar = ({
  image,
  name,
  className,
  size,
  fallback,
  shape,
  type,
}: AvatarProps & VariantProps<typeof avatarStyles>) => {
  const isGCS = image?.startsWith('gs');
  const [url, setUrl] = useState<GCSUrl>(isGCS ? undefined : image);

  async function getDownloadUrl() {
    const storage = getStorage();
    const storageRef = ref(storage, image);

    setUrl(await getDownloadURL(storageRef));
  }

  useEffect(() => {
    if (isGCS) {
      getDownloadUrl();
    } else {
      setUrl(image);
    }
  }, [image]);

  return (
    <div
      className={avatarStyles({
        className,
        avatar: !!image,
        size,
        shape,
        type,
      })}
    >
      {url ? (
        <img
          src={url}
          referrerPolicy='no-referrer'
          className='object-contain'
        />
      ) : fallback ? (
        fallback
      ) : (
        <p className='text-blue-900 font-medium'>{name[0]?.toUpperCase()}</p>
      )}
    </div>
  );
};
